import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowRight, Check, Calendar, Mail } from "lucide-react";
import { BOOKING } from "@/constants/testIds";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INDUSTRIES = [
  "Healthcare receivables",
  "Legal",
  "Financial services",
  "Other",
];

export default function BookingCTA() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    industry: "",
    message: "",
  });
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const setField = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
  const canSubmit =
    form.name.trim().length > 0 &&
    validEmail &&
    form.company.trim().length > 0 &&
    (status === "idle" || status === "error");

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      setStatus("error");
      setErrorMsg("Add your name, work email, and company.");
      return;
    }
    setStatus("loading");
    setErrorMsg("");
    try {
      await axios.post(`${API}/intro-call`, {
        name: form.name,
        email: form.email,
        company: form.company,
        industry: form.industry || null,
        message: form.message || null,
        source: "landing",
      });
      setStatus("success");
      toast.success("Booked. We'll reach out within one business day.");
    } catch (err) {
      setStatus("error");
      const detail =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Try again.";
      setErrorMsg(typeof detail === "string" ? detail : "Something went wrong.");
      toast.error("We couldn't send that. Try again.");
    }
  };

  return (
    <section
      id="book"
      data-testid={BOOKING.root}
      className="relative bg-[#ffffff] border-t border-black/10 overflow-hidden"
    >
      <div className="absolute inset-0 pointer-events-none opacity-50">
        <div className="absolute -top-20 left-1/2 -translate-x-1/2 h-[600px] w-[900px] rounded-full bg-black/[0.03] blur-[120px]" />
      </div>

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-36">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* LEFT */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 font-mono text-[12px] uppercase tracking-[0.3em] text-black/65 mb-6">
              <span className="eyebrow-bar" aria-hidden="true" />
              <span>Book a call</span>
            </div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-[64px] leading-[0.98] tracking-tightest text-[#0a0a0a]"
            >
              Give us 20 minutes
              <span className="block italic font-light text-black/65">
                to show how we'd run one of your processes.
              </span>
            </motion.h2>
            <p className="mt-8 font-sans text-black/70 text-base md:text-lg leading-relaxed max-w-md">
              Active clients across healthcare receivables, legal, and financial
              services.
            </p>

            <div className="mt-10 flex flex-col gap-3 max-w-sm">
              <a
                href="#book"
                data-testid={BOOKING.fallbackLink}
                onClick={(e) => {
                  e.preventDefault();
                  document
                    .querySelector(`[data-testid=${BOOKING.name}]`)
                    ?.focus();
                }}
                className="font-mono text-[12px] uppercase tracking-[0.22em] text-[#0a0a0a] hover:text-black transition-colors inline-flex items-center gap-2"
              >
                <Calendar size={13} />
                Open the booking calendar
                <ArrowRight size={13} />
              </a>
              <a
                href="mailto:avery@arvios.com"
                className="font-mono text-[12px] uppercase tracking-[0.22em] text-black/60 hover:text-black transition-colors inline-flex items-center gap-2"
              >
                <Mail size={13} />
                Prefer email? avery@arvios.com
              </a>
            </div>
          </div>

          {/* RIGHT — Form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              data-testid={BOOKING.form}
              noValidate
              className="border border-black/15 p-6 md:p-10 bg-white shadow-[0_30px_80px_-40px_rgba(10,10,10,0.18)]"
            >
              <div className="font-mono text-[11px] uppercase tracking-[0.28em] text-black/65 mb-8">
                Request my intro call · ARV-FORM-01
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-black/15 border border-black/15">
                <Field
                  label="Your name"
                  testId={BOOKING.name}
                  value={form.name}
                  onChange={setField("name")}
                  placeholder="Avery Reed"
                  required
                />
                <Field
                  label="Work email"
                  testId={BOOKING.email}
                  type="email"
                  value={form.email}
                  onChange={setField("email")}
                  placeholder="avery@firm.com"
                  required
                />
                <Field
                  label="Company"
                  testId={BOOKING.company}
                  value={form.company}
                  onChange={setField("company")}
                  placeholder="Firm or organization"
                  required
                />
                <SelectField
                  label="Industry"
                  testId={BOOKING.industry}
                  value={form.industry}
                  onChange={setField("industry")}
                  options={INDUSTRIES}
                />
              </div>

              <div className="border border-black/15 border-t-0">
                <TextAreaField
                  label="What process should we look at?"
                  testId={BOOKING.message}
                  value={form.message}
                  onChange={setField("message")}
                  placeholder="A sentence or two is plenty."
                />
              </div>

              <button
                type="submit"
                data-testid={BOOKING.submit}
                disabled={status === "loading" || status === "success"}
                className="mt-8 btn-primary w-full justify-center disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {status === "loading" && "Sending…"}
                {status === "success" && (
                  <>
                    Booked <Check size={14} />
                  </>
                )}
                {(status === "idle" || status === "error") && (
                  <>
                    Book My Intro Call <ArrowRight size={14} />
                  </>
                )}
              </button>

              <div className="mt-4 min-h-[24px] font-mono text-[12px] uppercase tracking-[0.22em]">
                {status === "idle" && (
                  <span className="text-black/60">
                    · 20 minutes · no obligation
                  </span>
                )}
                {status === "loading" && (
                  <span className="text-black/65">· Sending…</span>
                )}
                {status === "error" && (
                  <span data-testid={BOOKING.error} className="text-red-600">
                    · {errorMsg}
                  </span>
                )}
                {status === "success" && (
                  <span data-testid={BOOKING.success} className="text-black/70">
                    · We'll reach out within one business day.
                  </span>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, testId, value, onChange, placeholder, type = "text", required }) {
  return (
    <label className="bg-white px-5 py-4 flex flex-col gap-2 group focus-within:bg-[#ffffff] transition-colors">
      <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-black/65">
        {label}
        {required ? " *" : ""}
      </span>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={testId}
        className="bg-transparent text-[#0a0a0a] placeholder:text-black/30 font-sans text-base outline-none"
      />
    </label>
  );
}

function SelectField({ label, testId, value, onChange, options }) {
  return (
    <label className="bg-white px-5 py-4 flex flex-col gap-2 focus-within:bg-[#ffffff] transition-colors">
      <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-black/65">
        {label}
      </span>
      <select
        value={value}
        onChange={onChange}
        data-testid={testId}
        className="bg-transparent text-[#0a0a0a] font-sans text-base outline-none [&>option]:bg-white"
      >
        <option value="">Select…</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </label>
  );
}

function TextAreaField({ label, testId, value, onChange, placeholder }) {
  return (
    <label className="bg-white px-5 py-4 flex flex-col gap-2 focus-within:bg-[#ffffff] transition-colors">
      <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-black/65">
        {label}
      </span>
      <textarea
        rows={3}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={testId}
        className="bg-transparent text-[#0a0a0a] placeholder:text-black/30 font-sans text-base outline-none resize-none"
      />
    </label>
  );
}
